import React from 'react';
import { endOfDay, isAfter } from 'date-fns';
import { coerceToDate, formatDate, formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';

import { Box, Button, ButtonGroup, Heading, Modal } from '@zillow/constellation-10';
import Page from '@renter-hub/components/RenterPage';
import PaymentFrequencyPage from './modules/PaymentFrequencyPage';
import ManualSchedulingPage from './modules/ManualSchedulingPage';
import OneTimeConfirmationPage from './modules/OneTimeConfirmationPage';
import RecurringSchedulingPage from './modules/RecurringSchedulingPage';
import RecurringConfirmationPage from './modules/RecurringConfirmationPage';
import OverdueSchedulingPage from './modules/OverdueSchedulingPage';
import SelectPaymentMethodsPage from './modules/SelectPaymentMethodsPage';
import NotificationContainer from '@common/components/NotificationContainer';
import HelmetContainer from '@common/components/HelmetContainer';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import SeoConstants from '@renter-hub/constants/seoConstants';
import userPersonas from '@common/constants/userPersonas';
import { AUTO_FREQUENCY, MANUAL_FREQUENCY, RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';
import PaymentMethod from '@common/constants/PaymentMethod';
import PontoonConstants from '@common/constants/PontoonExceptionConstants';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import { getFaqs, pageFlows } from './pageFlows';
import type {
  RenterNextPayableInterface,
  RenterNextScheduleMonthInterface,
  RenterPaymentDetailsInterface,
  RenterPaymentMethodInterface,
  RenterRentTermsInterface,
} from '@renter-hub/models/Types';
import type { CreatePaymentSubscriptionWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  today: Date;
  fromDashboard: boolean;
  startPage: string;
  encodedPaymentMethodId?: string;
  requestInitiatePayment: (
    arg0: CreatePaymentSubscriptionWebApiRequestDto,
    isOverdue?: boolean,
    selectedDate?: string,
  ) => any;
  onBack: () => void;
  closeNotification: () => void;
  onAddNewPaymentClick: (onAddNewPaymentClick: string) => void;
  paymentDetails: RenterPaymentDetailsInterface;
  rentTerms: RenterRentTermsInterface;
  nextPayable: RenterNextPayableInterface | RenterNextScheduleMonthInterface;
  isMICPresent: boolean;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  pageFlowOverride?: keyof typeof pageFlows;
  savedFrequencyType?: typeof MANUAL_FREQUENCY | typeof AUTO_FREQUENCY;
  user: ZrmClickstreamUser;
  listingAlias?: string;
}

interface State {
  currentPage: string;
  selectedDate: string;
  showSelect?: boolean;
  frequencyType: string;
  isLoading?: boolean;
  selectedPaymentMethod?: RenterPaymentMethodInterface;
}

class PaymentSettingsPageV2 extends React.Component<Props, State> {
  static defaultProps = {
    today: new Date(),
    fromDashboard: false,
    startPage: pageFlows.FrequencyPage,
    isMICPresent: false,
    requestInitiatePayment: () => {},
    onBack: () => {},
  };

  get isOverdue() {
    const base = coerceToDate(this.props.nextPayable.dueDate);
    const dueDate = base ? endOfDay(base) : undefined;
    return !!dueDate && isAfter(this.props.today, dueDate);
  }

  constructor(props: Props) {
    super(props);
    const { startPage, paymentDetails, paymentMethods, nextPayable, pageFlowOverride, savedFrequencyType } = props;
    const { paymentSubscriptionDetails } = paymentDetails;

    const paymentSubscriptionIsManual = paymentSubscriptionDetails.recurring === false;

    const getFrequencyType = () => {
      if (savedFrequencyType) {
        return savedFrequencyType;
      } else {
        return startPage === pageFlows.ManualSchedulingPage || this.isOverdue || paymentSubscriptionIsManual
          ? MANUAL_FREQUENCY
          : AUTO_FREQUENCY;
      }
    };

    const getCurrentPage = () => {
      if (this.isOverdue) {
        return this.props.fromDashboard ? pageFlows.SelectPaymentMethodPage : pageFlows.OverduePage;
      } else if (pageFlowOverride) {
        return pageFlowOverride;
      } else {
        return startPage;
      }
    };

    this.state = {
      currentPage: getCurrentPage(),
      selectedDate: nextPayable.dueDate,
      frequencyType: getFrequencyType(),
      selectedPaymentMethod: paymentMethods?.find(
        (paymentMethod) => paymentMethod.id === paymentSubscriptionDetails.paymentMethodId,
      ),
    };
  }

  handleClickstreamBackEvent = () => {
    const currentPage = this.state.currentPage;
    const trackingEvents = {
      [pageFlows.FrequencyPage]: 'payment_settings_cadence_page',
      [pageFlows.ManualSchedulingPage]: 'payment_settings_scheduling_page',
      [pageFlows.RecurringSchedulingPage]: 'payment_settings_scheduling_page',
      [pageFlows.OneTimeConfirmationPage]: 'payment_settings_confirmation_page',
      [pageFlows.RecurringConfirmationPage]: 'payment_settings_confirmation_page',
    };

    RachelCSEvents.emitClickBackButtonEvent({ user: this.props.user, triggerObject: trackingEvents[currentPage] });
  };

  togglePrevPage = () => {
    const { currentPage } = this.state;

    if (!this.props.fromDashboard) {
      this.handleClickstreamBackEvent();
    }
    if (currentPage === pageFlows.OverduePage && this.props.fromDashboard) {
      this.setState({
        currentPage: pageFlows.SelectPaymentMethodPage,
      });
    } else if (
      currentPage === pageFlows.FrequencyPage ||
      currentPage === pageFlows.OverduePage ||
      (this.isOverdue && this.props.fromDashboard && currentPage === pageFlows.SelectPaymentMethodPage)
    ) {
      this.props.onBack();
    } else if (currentPage === pageFlows.RecurringConfirmationPage) {
      this.setState({
        currentPage: pageFlows.SelectPaymentMethodPage,
      });
    } else if (currentPage === pageFlows.OneTimeConfirmationPage) {
      this.setState({
        currentPage: pageFlows.SelectPaymentMethodPage,
      });
    } else {
      this.setState({
        currentPage: pageFlows.FrequencyPage,
      });
    }
  };

  toggleNextPage = () => {
    const { currentPage, frequencyType } = this.state;

    if (currentPage === pageFlows.FrequencyPage) {
      if (this.props.fromDashboard) {
        RachelCSEvents.emitClickContinueFromCadencePageEvent({
          user: this.props.user,
          triggerSource: this.state.frequencyType === MANUAL_FREQUENCY ? 'continue_onetime' : 'continue_autopay',
        });
      } else {
        RachelCSEvents.emitClickContinueFromPaymentSettingsCadencePageEvent({
          user: this.props.user,
          currentProperty: { listingAlias: this.props.listingAlias },
          triggerSource: this.state.frequencyType === MANUAL_FREQUENCY ? 'continue_onetime' : 'continue_autopay',
        });
      }
      const nextPage =
        frequencyType === MANUAL_FREQUENCY ? pageFlows.ManualSchedulingPage : pageFlows.RecurringSchedulingPage;
      this.setState({ currentPage: nextPage });
    } else if (currentPage === pageFlows.ManualSchedulingPage && this.props.fromDashboard) {
      this.setState({
        currentPage: pageFlows.SelectPaymentMethodPage,
      });
      if (this.props.fromDashboard) {
        RachelCSEvents.emitClicksContinueFromSchedulingPageEvent({ user: this.props.user });
      }
    } else if (currentPage === pageFlows.RecurringSchedulingPage) {
      if (this.props.fromDashboard) {
        RachelCSEvents.emitClicksContinueFromSchedulingPageEvent({ user: this.props.user });
      } else {
        RachelCSEvents.emitClickContinueFromPaymentSettingsSchedulingPageEvent({
          user: this.props.user,
          currentProperty: { listingAlias: this.props.listingAlias },
        });
      }
      const nextPage = this.props.fromDashboard
        ? pageFlows.SelectPaymentMethodPage
        : pageFlows.RecurringConfirmationPage;
      this.setState({
        currentPage: nextPage,
      });
    } else if (currentPage === pageFlows.SelectPaymentMethodPage && this.props.fromDashboard) {
      if (this.isOverdue) {
        this.setState({ currentPage: pageFlows.OverduePage });
      } else {
        const nextConfirmPage =
          frequencyType === MANUAL_FREQUENCY ? pageFlows.OneTimeConfirmationPage : pageFlows.RecurringConfirmationPage;
        this.setState({ currentPage: nextConfirmPage });
        this.props.closeNotification();
      }

      if (this.props.fromDashboard) {
        RachelCSEvents.emitClicksContinueInSelectPaymentModalEvent({ user: this.props.user });
      }
    }
  };

  setFrequencyType = (frequencyType: string) => {
    this.setState({
      frequencyType,
    });
  };

  changeSelectedDate = (newSelectedDate) => {
    this.setState({
      selectedDate: formatDate(newSelectedDate, 'yyyy-MM-dd'),
      showSelect: false,
    });
  };

  requestInitiatePayment = async () => {
    const { nextPayable, fromDashboard, encodedPaymentMethodId } = this.props;
    const { frequencyType, selectedDate, selectedPaymentMethod } = this.state;
    await this.setState({
      isLoading: true,
    });

    const payload: CreatePaymentSubscriptionWebApiRequestDto = {
      scheduleMonth: nextPayable.scheduleMonth,
      paymentDate: formatYMDStringToTimestamp(selectedDate),
      recurring: frequencyType === AUTO_FREQUENCY,
      encodedPaymentMethodId: fromDashboard ? selectedPaymentMethod?.id : encodedPaymentMethodId,
    };

    if (this.props.fromDashboard) {
      RachelCSEvents.emitClicksScheduleRecurringRentPaymentEvent({ user: this.props.user });
    } else {
      RachelCSEvents.emitClickContinueFromPaymentSettingsConfirmationPageEvent({
        user: this.props.user,
        currentProperty: { listingAlias: this.props.listingAlias },
      });
    }

    const response = await this.props.requestInitiatePayment(payload, this.isOverdue, selectedDate);

    if (response.error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  handleSelectPaymentMethodSelect = (data: RenterPaymentMethodInterface) => {
    this.setState({
      selectedPaymentMethod: data,
    });
  };

  renderMainSection = () => {
    const {
      today,
      fromDashboard,
      startPage,
      nextPayable,
      rentTerms,
      isMICPresent,
      paymentDetails: { accountType },
    } = this.props;
    const { currentPage, frequencyType, selectedDate } = this.state;
    const { monthToMonth = false } = rentTerms;
    const paymentMethodIsBank = accountType === PaymentMethod.TYPE.BANK_ACCOUNT;

    switch (currentPage) {
      case pageFlows.FrequencyPage:
      default:
        return {
          header: 'How would you like to send your rent payments?',
          body: <PaymentFrequencyPage frequencyType={frequencyType} setFrequencyType={this.setFrequencyType} />,
          footer: (
            <>
              {!this.props.fromDashboard && (
                <Button onClick={this.togglePrevPage}>{isMICPresent ? 'Back' : 'Go to dashboard'}</Button>
              )}
              <Button emphasis="filled" onClick={this.toggleNextPage} loading={this.state.isLoading}>
                Continue
              </Button>
            </>
          ),
        };

      case pageFlows.ManualSchedulingPage:
        return {
          header: paymentMethodIsBank
            ? `When should your ${formatDate(
                nextPayable.dueDate,
                'MMMM do',
                PontoonConstants.RENTER_HUB.PAYMENTS_SETTINGS.RENT_PAYMENT.DUE_DATE,
              )} rent be withdrawn?`
            : `When should your card be charged for your ${formatDate(
                nextPayable.dueDate,
                'MMMM do',
                PontoonConstants.RENTER_HUB.PAYMENTS_SETTINGS.RENT_PAYMENT.DUE_DATE,
              )} rent?`,
          body: (
            <ManualSchedulingPage
              dueDate={nextPayable.dueDate}
              selectedDate={formatDate(selectedDate, 'MM/dd/yyyy')}
              changeSelectedDate={this.changeSelectedDate}
            />
          ),
          footer: (
            <>
              {this.props.startPage === pageFlows.FrequencyPage && <Button onClick={this.togglePrevPage}>Back</Button>}
              <DebouncedButton
                loading={this.state.isLoading}
                emphasis="filled"
                onClick={
                  this.props.startPage === pageFlows.FrequencyPage && this.props.fromDashboard
                    ? this.toggleNextPage
                    : this.requestInitiatePayment
                }
              >
                {this.props.startPage === pageFlows.FrequencyPage && this.props.fromDashboard
                  ? 'Continue'
                  : 'Schedule rent payment'}
              </DebouncedButton>
            </>
          ),
        };

      case pageFlows.OneTimeConfirmationPage:
        return {
          header: 'Confirm your one time payment details',
          body: (
            <OneTimeConfirmationPage
              selectedDate={selectedDate}
              paymentMethod={this.state.selectedPaymentMethod}
              dueAmount={rentTerms.amount}
              totalAmount={this.state.selectedPaymentMethod?.rentAmountDetails.totalAmount}
              fee={this.state.selectedPaymentMethod?.rentAmountDetails.fee}
            />
          ),
          footer: (
            <>
              <Button onClick={this.togglePrevPage}>Back</Button>
              <DebouncedButton
                emphasis="filled"
                onClick={() => this.requestInitiatePayment()}
                loading={this.state.isLoading}
              >
                Schedule payment
              </DebouncedButton>
            </>
          ),
        };

      case pageFlows.RecurringSchedulingPage:
        return {
          header: paymentMethodIsBank
            ? 'When should your rent be withdrawn each month?'
            : 'When should your card be charged each month?',
          body: (
            <RecurringSchedulingPage
              today={today}
              startPage={startPage}
              dueDate={nextPayable.dueDate}
              selectedDate={selectedDate}
              changeSelectedDate={this.changeSelectedDate}
              fromDashboard={fromDashboard}
            />
          ),
          footer: (
            <>
              {this.props.startPage === pageFlows.FrequencyPage && <Button onClick={this.togglePrevPage}>Back</Button>}
              <Button emphasis="filled" onClick={this.toggleNextPage} loading={this.state.isLoading}>
                Continue
              </Button>
            </>
          ),
        };

      case pageFlows.RecurringConfirmationPage:
        return {
          header: 'Confirm your recurring payment details',
          body: (
            <RecurringConfirmationPage
              selectedDate={selectedDate}
              numberOfPayments={nextPayable.numberOfPaymentsRemaining}
              monthToMonth={monthToMonth}
              paymentMethod={this.state.selectedPaymentMethod}
              dueAmount={rentTerms.amount}
              totalAmount={this.state.selectedPaymentMethod?.rentAmountDetails.totalAmount}
              fee={this.state.selectedPaymentMethod?.rentAmountDetails.fee}
            />
          ),
          footer: (
            <>
              <Button onClick={this.togglePrevPage}>Back</Button>
              <DebouncedButton emphasis="filled" onClick={this.requestInitiatePayment} loading={this.state.isLoading}>
                Schedule rent payment
              </DebouncedButton>
            </>
          ),
        };

      case pageFlows.SelectPaymentMethodPage:
        return {
          header: 'Select a payment method',
          body: (
            <SelectPaymentMethodsPage
              handleSelectPaymentMethodSelect={this.handleSelectPaymentMethodSelect}
              handleAddNewPaymentClick={() => this.props.onAddNewPaymentClick(this.state.frequencyType)}
              previouslySelectedPaymentMethod={this.state.selectedPaymentMethod}
            />
          ),
          footer: (
            <>
              <Button onClick={this.togglePrevPage}>Back</Button>
              <Button
                loading={this.state.isLoading}
                emphasis="filled"
                onClick={this.toggleNextPage}
                disabled={!this.state.selectedPaymentMethod}
              >
                Continue
              </Button>
            </>
          ),
        };

      case pageFlows.OverduePage:
        return {
          header: `Your ${formatDate(
            nextPayable.dueDate,
            'MMMM do',
            PontoonConstants.RENTER_HUB.PAYMENTS_SETTINGS.RENT_PAYMENT.DUE_DATE,
          )} rent`,
          body: <OverdueSchedulingPage dueDate={nextPayable.dueDate} />,
          footer: (
            <>
              <Button onClick={this.togglePrevPage}>{isMICPresent ? 'Back' : 'Go to dashboard'}</Button>
              <DebouncedButton loading={this.state.isLoading} emphasis="filled" onClick={this.requestInitiatePayment}>
                Send rent payment now
              </DebouncedButton>
            </>
          ),
        };
    }
  };

  renderFaqSection = () => {
    const { fromDashboard } = this.props;
    const { currentPage } = this.state;
    const faqs = getFaqs(currentPage);
    return faqs ? <FaqComponent faqs={faqs.body()} withBorder={!fromDashboard} showContactSection={false} /> : null;
  };

  renderPage = (pageDetails) => {
    const progressBarProps = { progressTitle: RENT_PAYMENT };
    return (
      <Page {...progressBarProps}>
        <HelmetContainer {...SeoConstants.paymentSettingsPage} />

        <Page.MainSection>
          <Heading level={1} textStyle="heading-xs" css={{ marginBottom: 'loose' }}>
            {pageDetails.header}
          </Heading>
          <Box css={{ marginBottom: 'default' }}>
            <NotificationContainer modal persona={userPersonas.RENTER} />
          </Box>
          {pageDetails.body}
          <Box
            css={{
              display: 'flex',
              gap: 'tighter',
              marginTop: 'loose',
              '& > *': {
                flexBasis: '50%',
              },
            }}
          >
            {pageDetails.footer}
          </Box>
        </Page.MainSection>
        <Page.RightSection>{this.renderFaqSection()}</Page.RightSection>
        <Page.BottomSection>{this.renderFaqSection()}</Page.BottomSection>
      </Page>
    );
  };

  renderModal = (pageDetails) => {
    const faqs = this.renderFaqSection();
    return (
      <Modal
        size={{ base: 'fullScreen', lg: 'md' }}
        open={true}
        trigger={null}
        header={<Heading level={2}>{pageDetails.header}</Heading>}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            this.props.onBack();
          }
        }}
        body={
          <Box>
            <HelmetContainer {...SeoConstants.paymentSettingsPage} />
            <Box css={{ marginBottom: 'default' }}>
              <NotificationContainer modal persona={userPersonas.RENTER} />
            </Box>
            {pageDetails.body}
            {faqs && <Box css={{ marginTop: 'loose' }}>{faqs}</Box>}
          </Box>
        }
        footer={<ButtonGroup aria-label="action-buttons">{pageDetails.footer}</ButtonGroup>}
      />
    );
  };

  render() {
    const { fromDashboard } = this.props;
    const pageDetails = this.renderMainSection();
    if (fromDashboard) {
      return this.renderModal(pageDetails);
    }
    return this.renderPage(pageDetails);
  }
}

export default PaymentSettingsPageV2;
